/**
 * Route and element guards. They only hide UI — the backend must reject any request the user may not make.
 */
import type { ReactNode } from 'react';
import type { Permission } from '@/types/models';
import { useAuth, useToast } from '@/app/AppContext';
import { ErrorState, LoadingState, Topbar } from '@/ui/kit';

/** Requires a signed-in user, and (optionally) one of the given permissions. */
export function ProtectedRoute({ children, anyOf }: { children: ReactNode; anyOf?: Permission[] }) {
  const auth = useAuth();
  const toast = useToast();
  if (auth.loading) return <LoadingState label="Checking access…" />;
  if (auth.error) return <ErrorState title="Could not check who is signed in." error={auth.error} onRetry={() => location.reload()} />;
  if (!auth.user)
    return (
      <>
        <Topbar title="Log In required" />
        <div className="panel">
          <div className="empty">
            <div>You are logged out.</div>
            <button type="button" className="btn primary" style={{ marginTop: 12 }} onClick={() => auth.logIn().catch((e: Error) => toast(e.message, true))}>
              Log In
            </button>
          </div>
        </div>
      </>
    );
  if (anyOf && !anyOf.some((p) => auth.can(p)))
    return (
      <>
        <Topbar title="Access denied" />
        <div className="panel">
          <div className="empty" role="alert">
            You do not have access to this page. Ask an administrator if you need it.
          </div>
        </div>
      </>
    );
  return <>{children}</>;
}

/** Renders children only when the user has the permission (e.g. hide "+ Add Project" for read-only roles). */
export function PermissionGate({ perm, children, fallback = null }: { perm: Permission; children: ReactNode; fallback?: ReactNode }) {
  const auth = useAuth();
  return <>{auth.can(perm) ? children : fallback}</>;
}

export const useCan = () => useAuth().can;
